import React, { useState, useRef, useMemo } from "react";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { useToast } from "../contexts/ToastContext";
import { usePeopleContext } from "../contexts/PeopleContext";
import {
  SavedFilter,
  FILTER_COLORS,
  saveFilter,
  updateFilter,
  deleteFilter,
  getSavedFilters,
  isFilterNameTaken,
  getFilterColor,
} from "../utils/filterUtils";

interface FilterManagerProps {
  activeFilter: SavedFilter | null;
  onFilterChange: (filter: SavedFilter | null) => void;
}

export const FilterManager: React.FC<FilterManagerProps> = ({
  activeFilter,
  onFilterChange,
}) => {
  // Get people data from context
  const { people } = usePeopleContext();

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(
    getSavedFilters()
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Form state
  const [filterName, setFilterName] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [selectedColor, setSelectedColor] = useState(FILTER_COLORS[0].value);
  const [nameError, setNameError] = useState("");

  const overlayRef = useRef<OverlayPanel>(null);
  const toast = useToast();

  // Transform people data for MultiSelect - sorted alphabetically
  const employeeOptions = useMemo(
    () =>
      people
        .map((person) => ({
          label: `${person.name} ${person.surname}`,
          value: person.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [people]
  );

  // Transform colors for Dropdown
  const colorOptions = FILTER_COLORS.map((color) => ({
    label: color.name,
    value: color.value,
    color: color.value,
  }));

  const resetForm = () => {
    setFilterName("");
    setSelectedEmployees([]);
    setSelectedColor(FILTER_COLORS[0].value);
    setNameError("");
    setEditingFilter(null);
  };

  const startNewFilter = (event: React.MouseEvent) => {
    resetForm();
    setIsEditing(true);
    overlayRef.current?.show(event, event.currentTarget);
  };

  const startEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setFilterName(filter.name);
    setSelectedEmployees([...filter.employeeIds]);
    setSelectedColor(filter.color);
    setNameError("");
    setIsEditing(true);
  };

  const showFilterManager = (event: React.MouseEvent) => {
    setIsEditing(false);
    overlayRef.current?.show(event, event.currentTarget);
  };

  const validateForm = (): boolean => {
    if (!filterName.trim()) {
      setNameError("Filter name is required");
      return false;
    }

    if (isFilterNameTaken(filterName.trim(), editingFilter?.id)) {
      setNameError("Filter name already exists");
      return false;
    }

    if (selectedEmployees.length === 0) {
      setNameError("Please select at least one employee");
      return false;
    }

    setNameError("");
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    try {
      let savedFilter: SavedFilter;

      if (editingFilter) {
        // Update existing filter
        savedFilter = updateFilter(editingFilter.id, {
          name: filterName.trim(),
          color: selectedColor,
          employeeIds: selectedEmployees,
        })!;
      } else {
        // Create new filter
        savedFilter = saveFilter({
          name: filterName.trim(),
          color: selectedColor,
          employeeIds: selectedEmployees,
        });
      }

      // Update local state
      setSavedFilters(getSavedFilters());

      // Apply the saved filter
      onFilterChange(savedFilter);

      // Close overlay and reset form
      overlayRef.current?.hide();
      setIsEditing(false);
      resetForm();

      toast.showSuccess(
        editingFilter ? "Filter Updated" : "Filter Saved",
        `Filter "${savedFilter.name}" has been ${
          editingFilter ? "updated" : "saved"
        } successfully`
      );
    } catch (error) {
      toast.showError("Error", "Failed to save filter");
    }
  };

  const handleDelete = (filter: SavedFilter) => {
    if (deleteFilter(filter.id)) {
      setSavedFilters(getSavedFilters());

      // Clear active filter if it was the deleted one
      if (activeFilter?.id === filter.id) {
        onFilterChange(null);
      }

      toast.showSuccess(
        "Filter Deleted",
        `Filter "${filter.name}" has been deleted`
      );
    }
  };

  const confirmDelete = (filter: SavedFilter) => {
    if (
      window.confirm(
        `Are you sure you want to delete the filter "${filter.name}"?`
      )
    ) {
      handleDelete(filter);
    }
  };

  const handleCancel = () => {
    overlayRef.current?.hide();
    setIsEditing(false);
    resetForm();
  };

  const clearActiveFilter = () => {
    onFilterChange(null);
  };

  // Color template for dropdown
  const colorTemplate = (option: any) => {
    if (!option) return null;
    return (
      <div className="flex align-items-center gap-2">
        <div
          className="w-1rem h-1rem border-round"
          style={{ backgroundColor: option.color }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <>
      <div className="filter-manager flex align-items-center gap-2">
        {/* Active Filter Display */}
        {activeFilter && (
          <Tag
            value={`Filter: ${activeFilter.name}`}
            style={{
              backgroundColor: getFilterColor(activeFilter.color).light,
              color: activeFilter.color,
              border: `1px solid ${activeFilter.color}`,
            }}
            icon="pi pi-filter"
          >
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-sm"
              style={{
                color: activeFilter.color,
                padding: "0.25rem",
                marginLeft: "0.5rem",
              }}
              onClick={clearActiveFilter}
              tooltip="Clear filter"
            />
          </Tag>
        )}

        {/* Filter Button */}
        <Button
          label={activeFilter ? "Change Filter" : "Filter"}
          icon="pi pi-filter"
          className="p-button-outlined p-button-sm"
          onClick={showFilterManager}
        />

        {/* Filter Management Overlay */}
        <OverlayPanel ref={overlayRef} style={{ width: "450px" }}>
          {isEditing ? (
            /* Edit/Create Filter Form */
            <Card title={editingFilter ? "Edit Filter" : "Create New Filter"}>
              <div className="flex flex-column gap-3">
                <div className="field">
                  <label
                    htmlFor="filter-name"
                    className="block text-sm font-medium mb-2"
                  >
                    Filter Name
                  </label>
                  <InputText
                    id="filter-name"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder="Enter filter name"
                    className={nameError ? "p-invalid w-full" : "w-full"}
                  />
                  {nameError && <small className="p-error">{nameError}</small>}
                </div>

                <div className="field">
                  <label
                    htmlFor="filter-employees"
                    className="block text-sm font-medium mb-2"
                  >
                    Select Employees
                  </label>
                  <MultiSelect
                    id="filter-employees"
                    value={selectedEmployees}
                    options={employeeOptions}
                    onChange={(e) => setSelectedEmployees(e.value)}
                    placeholder="Select employees"
                    className="w-full"
                    display="chip"
                    maxSelectedLabels={3}
                    filter
                    filterInputAutoFocus
                  />
                </div>

                <div className="field">
                  <label
                    htmlFor="filter-color"
                    className="block text-sm font-medium mb-2"
                  >
                    Filter Color
                  </label>
                  <Dropdown
                    id="filter-color"
                    value={selectedColor}
                    options={colorOptions}
                    onChange={(e) => setSelectedColor(e.value)}
                    valueTemplate={colorTemplate}
                    itemTemplate={colorTemplate}
                    className="w-full"
                  />
                </div>

                <div className="flex gap-2 justify-content-end">
                  <Button
                    label="Cancel"
                    className="p-button-outlined"
                    onClick={handleCancel}
                  />
                  <Button
                    label={editingFilter ? "Update" : "Save"}
                    onClick={handleSave}
                  />
                </div>
              </div>
            </Card>
          ) : (
            /* Saved Filters Management */
            <Card title="Filters">
              <div className="flex flex-column gap-3">
                {savedFilters.length === 0 ? (
                  <p className="text-center text-500 mb-3">
                    No saved filters yet
                  </p>
                ) : (
                  <div className="flex flex-column gap-2 mb-3">
                    {savedFilters.map((filter) => (
                      <div
                        key={filter.id}
                        className="flex align-items-center justify-content-between p-2 border-round surface-100"
                      >
                        <div className="flex align-items-center gap-2">
                          <div
                            className="w-1rem h-1rem border-round"
                            style={{ backgroundColor: filter.color }}
                          />
                          <span className="font-medium">{filter.name}</span>
                          <small className="text-500">
                            ({filter.employeeIds.length} employees)
                          </small>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            icon="pi pi-check"
                            className="p-button-text p-button-sm"
                            onClick={() => {
                              onFilterChange(filter);
                              overlayRef.current?.hide();
                            }}
                            tooltip="Apply filter"
                          />
                          <Button
                            icon="pi pi-pencil"
                            className="p-button-text p-button-sm"
                            onClick={() => startEditFilter(filter)}
                            tooltip="Edit filter"
                          />
                          <Button
                            icon="pi pi-trash"
                            className="p-button-text p-button-sm p-button-danger"
                            onClick={() => confirmDelete(filter)}
                            tooltip="Delete filter"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    label="New Filter"
                    icon="pi pi-plus"
                    className="p-button-outlined flex-1"
                    onClick={startNewFilter}
                  />
                  {activeFilter && (
                    <Button
                      label="Clear"
                      icon="pi pi-times"
                      className="p-button-outlined p-button-secondary"
                      onClick={() => {
                        clearActiveFilter();
                        overlayRef.current?.hide();
                      }}
                    />
                  )}
                </div>
              </div>
            </Card>
          )}
        </OverlayPanel>
      </div>
    </>
  );
};
